import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'

import router from '@/router'

import { API_URL } from '@/helpers/api-url.helper'

const publicApi: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
})

const privateApi: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
})

privateApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('oper_access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

privateApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const { status, data } = error.response

      const isTokenError = status === 401 || (data?.code && ['1008', '1009'].includes(data.code))

      if (isTokenError) {
        localStorage.removeItem('oper_access_token')
        router.push('/auth/sign-in')
      }
    }

    return Promise.reject(error)
  },
)

const useApiClient = (isAuthenticated = false, baseUrl?: string) => {
  const api = isAuthenticated ? privateApi : publicApi

  if (baseUrl) {
    api.defaults.baseURL = baseUrl
  }

  return {
    get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
      const response: AxiosResponse<T> = await api.get(url, config)
      return response.data
    },
    post: async <T, R = unknown>(
      url: string,
      data?: R,
      config?: AxiosRequestConfig,
    ): Promise<T> => {
      const response: AxiosResponse<T> = await api.post(url, data, config)
      return response.data
    },

    put: async <T, R = unknown>(url: string, data?: R, config?: AxiosRequestConfig): Promise<T> => {
      const response: AxiosResponse<T> = await api.put(url, data, config)
      return response.data
    },
    patch: async <T, R = unknown>(
      url: string,
      data?: R,
      config?: AxiosRequestConfig,
    ): Promise<T> => {
      const response: AxiosResponse<T> = await api.patch(url, data, config)
      return response.data
    },
    delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
      const response: AxiosResponse<T> = await api.delete(url, config)
      return response.data
    },
  }
}

export { useApiClient }
